import { router } from '@inertiajs/react';
import { toast } from 'sonner';

import { buildLogoDraftSettings, type LogoDraftSettings } from '@/components/logo-builder/apply';
import type { SiteFooterSlotConfig, SiteHeaderSlotConfig } from '@/components/slot-settings/types';
import http, { extractApiErrorMessage } from '@/lib/http';
import {
  cmsSiteDesignPath,
  cmsSitesSlotSettingsPath,
  cmsSpecialPageLayoutPath,
} from '@/lib/routes';
import type { SpecialPageType } from '@/lib/special-page-layouts';
import type { ButtonSettings, ColorSettings, FontSettings } from '@/types/shared';
import { flushEditorHistoryWrite } from './editor-history-storage';
import {
  computeJsonChanged,
  type SiteStyles,
  type SpecialPageLayoutCodes,
} from './site-styles-shared';

const PERSIST_DEBOUNCE_MS = 400;

const logDebouncedPersistFailure = (error: unknown): void => {
  console.error('Debounced site styles persist failed', error);
};

interface Debouncer {
  schedule: (fn: () => Promise<void>) => void;
  flush: () => Promise<void>;
}

const createDebouncer = (): Debouncer => {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pendingFn: (() => Promise<void>) | null = null;
  let inflightPromise: Promise<void> | null = null;

  const run = (): Promise<void> => {
    const fn = pendingFn;
    pendingFn = null;
    if (!fn) return inflightPromise ?? Promise.resolve();

    const chain = (inflightPromise ?? Promise.resolve())
      .catch(logDebouncedPersistFailure)
      .then(() => fn())
      .finally(() => {
        if (inflightPromise === chain) inflightPromise = null;
      });
    inflightPromise = chain;
    return chain;
  };

  return {
    schedule(fn) {
      pendingFn = fn;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        run().catch(logDebouncedPersistFailure);
      }, PERSIST_DEBOUNCE_MS);
    },
    async flush() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      await run();
    },
  };
};

const debounceDesign = createDebouncer();
const debounceHeader = createDebouncer();
const debounceFooter = createDebouncer();

type PendingDesignPatch = {
  logoTagline?: string;
  colors?: ColorSettings;
  fonts?: FontSettings;
  buttons?: ButtonSettings;
  logo?: LogoDraftSettings;
};

let pendingDesignPatch: PendingDesignPatch = {};

const reportDesignPersistFailure = (design: PendingDesignPatch, error: unknown): void => {
  console.error('Failed to persist site styles draft', error);
  if (design.logo) {
    toast.error(extractApiErrorMessage(error) ?? 'Failed to save logo. Please try again.');
  }
};

const scheduleDesignPatch = (partial: PendingDesignPatch): void => {
  pendingDesignPatch = { ...pendingDesignPatch, ...partial };
  debounceDesign.schedule(() => {
    const design = pendingDesignPatch;
    pendingDesignPatch = {};

    return http
      .patch(cmsSiteDesignPath(), {
        settings: { design },
      })
      .then(() => {})
      .catch((error) => {
        reportDesignPersistFailure(design, error);
        throw error;
      });
  });
};

export async function flushPendingSiteStylesSaves(): Promise<void> {
  await Promise.all([debounceDesign.flush(), debounceHeader.flush(), debounceFooter.flush()]);
}

const persistDesign = (draft: SiteStyles, previousDraft?: SiteStyles): void => {
  const taglineChanged = previousDraft
    ? draft.logoTagline !== previousDraft.logoTagline
    : draft.logoTagline !== null;
  if (!draft.colors && !draft.fonts && !draft.buttons && !taglineChanged) return;

  scheduleDesignPatch({
    ...(taglineChanged ? { logoTagline: draft.logoTagline ?? '' } : {}),
    ...(draft.colors ? { colors: draft.colors } : {}),
    ...(draft.fonts ? { fonts: draft.fonts } : {}),
    ...(draft.buttons ? { buttons: draft.buttons } : {}),
  });
};

const persistSlot = (
  slotType: 'header' | 'footer',
  config: SiteHeaderSlotConfig | SiteFooterSlotConfig | null,
): void => {
  if (!config) return;
  const debouncer = slotType === 'header' ? debounceHeader : debounceFooter;
  debouncer.schedule(() =>
    http
      .patch(cmsSitesSlotSettingsPath(), { slotType, slotSettings: config })
      .then(() => {})
      .catch((error) => {
        console.error(`Failed to persist ${slotType} slot draft`, error);
        throw error;
      }),
  );
};

const persistLogoSettings = (draftSettings: LogoDraftSettings): void => {
  scheduleDesignPatch({ logo: draftSettings });
};

export const persistSpecialPageLayout = (pageType: SpecialPageType, code: string): void => {
  void http
    .patch(cmsSpecialPageLayoutPath(), { pageType, sectionTemplateCode: code })
    .then(() => flushPendingSiteStylesSaves())
    .then(() => {
      flushEditorHistoryWrite();
      router.reload();
    })
    .catch((error) => {
      console.error('Failed to persist special page layout draft', error);
    });
};

const SPECIAL_PAGE_LAYOUT_KEYS: ReadonlyArray<[keyof SpecialPageLayoutCodes, SpecialPageType]> = [
  ['caseResult', 'case_result'],
  ['blog', 'blog'],
];

const persistSpecialPageLayoutsDiff = (
  draft: SiteStyles,
  previousDraft: SiteStyles | undefined,
): void => {
  const current = draft.specialPageLayouts;
  const previous = previousDraft?.specialPageLayouts ?? null;

  for (const [key, pageType] of SPECIAL_PAGE_LAYOUT_KEYS) {
    const code = current?.[key] ?? null;
    if (code && code !== (previous?.[key] ?? null)) {
      persistSpecialPageLayout(pageType, code);
    }
  }
};

const computeColorsChanged = (
  originalColors: ColorSettings | null,
  draftColors: ColorSettings | null,
): boolean => {
  if (originalColors === null && draftColors === null) return false;
  if (originalColors === null || draftColors === null) return true;
  const colorKeys = new Set([...Object.keys(originalColors), ...Object.keys(draftColors)]);
  return Array.from(colorKeys).some(
    (key) => originalColors[key as keyof ColorSettings] !== draftColors[key as keyof ColorSettings],
  );
};

const computeFontsChanged = (
  originalFonts: FontSettings | null,
  draftFonts: FontSettings | null,
): boolean => {
  if (originalFonts === null && draftFonts === null) return false;
  if (originalFonts === null || draftFonts === null) return true;
  return JSON.stringify(originalFonts) !== JSON.stringify(draftFonts);
};

const computeButtonsChanged = (
  originalButtons: ButtonSettings | null,
  draftButtons: ButtonSettings | null,
): boolean => {
  if (originalButtons === null && draftButtons === null) return false;
  if (originalButtons === null || draftButtons === null) return true;
  return (
    originalButtons.style !== draftButtons.style || originalButtons.shape !== draftButtons.shape
  );
};

export const persistDraft = (draft: SiteStyles, previousDraft: SiteStyles | undefined): void => {
  const nextLogoSettings = buildLogoDraftSettings(draft.logo);
  const previousLogoSettings = previousDraft ? buildLogoDraftSettings(previousDraft.logo) : null;

  if (
    nextLogoSettings &&
    (!previousDraft || computeJsonChanged(previousLogoSettings, nextLogoSettings))
  ) {
    persistLogoSettings(nextLogoSettings);
  }

  const designChanged =
    !previousDraft ||
    computeColorsChanged(draft.colors, previousDraft.colors) ||
    computeColorsChanged(draft.persistedCustomColors, previousDraft.persistedCustomColors) ||
    computeFontsChanged(draft.fonts, previousDraft.fonts) ||
    draft.logoTagline !== previousDraft.logoTagline ||
    computeButtonsChanged(draft.buttons, previousDraft.buttons);
  if (designChanged) {
    persistDesign(draft, previousDraft);
  }

  if (!previousDraft || computeJsonChanged(draft.header, previousDraft.header)) {
    persistSlot('header', draft.header);
  }

  if (!previousDraft || computeJsonChanged(draft.footer, previousDraft.footer)) {
    persistSlot('footer', draft.footer);
  }

  persistSpecialPageLayoutsDiff(draft, previousDraft);
};

export { persistDesign, persistSlot };
